import React, { useEffect, useState } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { rememberPendingCheckout } from '../utils/paymentRecovery';

const RESEARCHER_BILLING = {
  quarterly: { price: '$69', sub: '/quarter', hint: 'Flexible short-term option', plan: 'quarterly' },
  yearly: { price: '$199', sub: '/year', hint: 'Best annual pricing', plan: 'annual' },
};

const FREE_FEATURES = [
  'Core labs and workflows',
  'Monthly free usage caps',
  'Account-linked progress tracking',
  'No card required',
];

const RESEARCHER_FEATURES = [
  'Unlimited Auto EDA, Document, Image, Speech, ML, and Video runs',
  'Priority report generation and exports',
  'Built-in agents, saved memory, and automation workflows',
  'Paid plan access across quarterly and yearly billing',
];

const LIFETIME_FEATURES = [
  'Everything in Researcher',
  'One-time payment, no renewals',
  'Lifetime supporter status',
  'All future premium upgrades included',
];

const cardStyle = {
  background: 'var(--surface)',
  borderRadius: '8px',
  padding: '28px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  border: '1px solid var(--b0)',
};

export default function PricingPage({ onAuthOpen }) {
  const { user, tier } = useAuth();
  const [billing, setBilling] = useState('quarterly');
  const [checkoutBusy, setCheckoutBusy] = useState('');
  const [billingStatus, setBillingStatus] = useState(null);

  const isResearcher = tier === 'researcher' || tier === 'annual' || tier === 'pro';
  const isLifetime = tier === 'lifetime';
  const canManageBilling = !!billingStatus?.customer_portal_url;
  const researcher = RESEARCHER_BILLING[billing];

  useEffect(() => {
    if (!user) {
      setBillingStatus(null);
      return undefined;
    }

    let cancelled = false;
    api.payments.status()
      .then((data) => {
        if (!cancelled) setBillingStatus(data);
      })
      .catch(() => {
        if (!cancelled) setBillingStatus(null);
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  const openBillingPortal = () => {
    const url = billingStatus?.customer_portal_url;
    if (url) window.location.assign(url);
  };

  const startCheckout = async (plan) => {
    if (!user) {
      onAuthOpen?.('login');
      return;
    }

    setCheckoutBusy(plan);
    try {
      const data = await api.payments.checkout(plan);
      if (data?.checkout_url) {
        rememberPendingCheckout(plan);
        window.location.assign(data.checkout_url);
      }
    } catch (err) {
      if (err?.status === 409 && err?.data?.customer_portal_url) {
        window.location.assign(err.data.customer_portal_url);
        return;
      }
      window.alert(err?.data?.error || err?.message || 'Failed to start checkout');
    } finally {
      setCheckoutBusy('');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--void)', paddingBottom: 80 }}>
      <section style={{ padding: '72px 24px 48px', textAlign: 'center', borderBottom: '1px solid var(--b0)' }}>
        <div style={{ display: 'inline-block', padding: '5px 14px', background: 'var(--cyan)12', border: '1px solid var(--cyan)33', borderRadius: 999, fontSize: 9, letterSpacing: '.18em', color: 'var(--cyan)', marginBottom: 20 }}>
          RESEARCH PLANS
        </div>
        <h1 style={{ fontFamily: 'Syne', fontWeight: 900, fontSize: 'clamp(32px,5vw,52px)', lineHeight: 1.08, margin: '0 0 16px' }}>
          Upgrade from free usage
          <br />
          to full Jorvex workflow access
        </h1>
        <p style={{ fontSize: 14, color: 'var(--t2)', maxWidth: 620, margin: '0 auto', lineHeight: 1.8 }}>
          Secure Lemon Squeezy checkout for quarterly, yearly, and lifetime access. Recurring plans can be managed from the billing portal at any time.
        </p>
      </section>

      <section style={{ padding: '56px 24px 0', maxWidth: 1080, margin: '0 auto' }}>
        <div className="dd-pricing-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 16 }}>
          <div style={cardStyle}>
            <div>
              <div style={{ fontSize: 9, color: 'var(--t3)', letterSpacing: '.14em', marginBottom: 8 }}>FREE</div>
              <div style={{ fontFamily: 'Syne', fontWeight: 900, fontSize: 38, color: 'var(--t1)' }}>$0</div>
              <div style={{ fontSize: 11, color: 'var(--t3)' }}>Good for trying the platform</div>
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 8, color: 'var(--t2)', fontSize: 12, lineHeight: 1.6 }}>
              {FREE_FEATURES.map((item) => <li key={item}>{item}</li>)}
            </ul>
            <button
              onClick={() => onAuthOpen?.('signup')}
              style={{ marginTop: 'auto', padding: '12px', background: 'var(--b0)', border: 'none', borderRadius: 6, color: 'var(--t1)', fontSize: 10, fontFamily: 'JetBrains Mono', cursor: 'pointer', letterSpacing: '.1em' }}
            >
              {user ? 'CURRENT PLAN' : 'CREATE FREE ACCOUNT'}
            </button>
          </div>

          <div style={{ ...cardStyle, border: '2px solid var(--cyan)', boxShadow: '0 20px 60px rgba(34,211,238,.12)' }}>
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: -40, right: 0, background: 'var(--cyan)', color: '#020508', fontSize: 9, fontFamily: 'Syne', fontWeight: 900, letterSpacing: '.14em', padding: '4px 12px', borderRadius: 999 }}>
                MOST POPULAR
              </div>
              <div style={{ fontSize: 9, color: 'var(--cyan)', letterSpacing: '.14em', marginBottom: 8 }}>RESEARCHER</div>
              <div style={{ display: 'inline-flex', gap: 6, marginBottom: 12 }}>
                {Object.entries(RESEARCHER_BILLING).map(([key, value]) => (
                  <button
                    key={key}
                    onClick={() => setBilling(key)}
                    style={{
                      padding: '5px 10px',
                      borderRadius: 999,
                      border: `1px solid ${billing === key ? 'var(--cyan)' : 'var(--b1)'}`,
                      background: billing === key ? 'var(--cyan)12' : 'transparent',
                      color: billing === key ? 'var(--cyan)' : 'var(--t3)',
                      fontSize: 8,
                      letterSpacing: '.14em',
                      fontFamily: 'JetBrains Mono',
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    {key === 'yearly' ? 'YEARLY' : 'QUARTERLY'}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <div style={{ fontFamily: 'Syne', fontWeight: 900, fontSize: 42, color: 'var(--cyan)' }}>{researcher.price}</div>
                <div style={{ fontSize: 12, color: 'var(--t3)' }}>{researcher.sub}</div>
              </div>
              <div style={{ marginTop: 6, fontSize: 11, color: 'var(--t3)' }}>{researcher.hint}</div>
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 8, color: 'var(--t2)', fontSize: 12, lineHeight: 1.6 }}>
              {RESEARCHER_FEATURES.map((item) => <li key={item}>{item}</li>)}
            </ul>
            {isResearcher || isLifetime ? (
              <>
                <div style={{ marginTop: 'auto', padding: 12, borderRadius: 6, background: 'var(--cyan)12', border: '1px solid var(--cyan)33', color: 'var(--cyan)', fontSize: 10, textAlign: 'center', letterSpacing: '.1em' }}>
                  ACTIVE ON YOUR ACCOUNT
                </div>
                {canManageBilling && (
                  <button
                    onClick={openBillingPortal}
                    style={{ padding: '12px', background: 'transparent', border: '1px solid var(--cyan)33', borderRadius: 6, color: 'var(--cyan)', fontSize: 10, fontFamily: 'JetBrains Mono', fontWeight: 800, cursor: 'pointer', letterSpacing: '.12em' }}
                  >
                    MANAGE BILLING
                  </button>
                )}
              </>
            ) : (
              <button
                onClick={() => startCheckout(researcher.plan)}
                style={{ marginTop: 'auto', padding: '14px', background: 'var(--cyan)', border: 'none', borderRadius: 6, color: '#020508', fontSize: 11, fontFamily: 'JetBrains Mono', fontWeight: 900, cursor: 'pointer', letterSpacing: '.12em' }}
              >
                {checkoutBusy === researcher.plan ? 'REDIRECTING...' : 'GET RESEARCHER ACCESS'}
              </button>
            )}
          </div>

          <div style={{ ...cardStyle, background: 'linear-gradient(135deg,var(--void) 0%,#0d1525 100%)', border: '2px solid var(--gold)66' }}>
            <div>
              <div style={{ fontSize: 9, color: 'var(--gold)', letterSpacing: '.14em', marginBottom: 8 }}>SUPPORTER</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <div style={{ fontFamily: 'Syne', fontWeight: 900, fontSize: 42, color: 'var(--gold)' }}>$420</div>
                <div style={{ fontSize: 12, color: 'var(--t3)' }}>once</div>
              </div>
              <div style={{ marginTop: 6, fontSize: 11, color: 'var(--t3)' }}>One-time payment. No renewals.</div>
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 8, color: 'var(--t2)', fontSize: 12, lineHeight: 1.6 }}>
              {LIFETIME_FEATURES.map((item) => <li key={item}>{item}</li>)}
            </ul>
            {isLifetime ? (
              <div style={{ marginTop: 'auto', padding: 12, borderRadius: 6, background: 'var(--gold)12', border: '1px solid var(--gold)33', color: 'var(--gold)', fontSize: 10, textAlign: 'center', letterSpacing: '.1em' }}>
                LIFETIME ACTIVE
              </div>
            ) : (
              <button
                onClick={() => startCheckout('lifetime')}
                style={{ marginTop: 'auto', padding: '14px', background: 'var(--gold)', border: 'none', borderRadius: 6, color: '#020508', fontSize: 11, fontFamily: 'JetBrains Mono', fontWeight: 900, cursor: 'pointer', letterSpacing: '.12em' }}
              >
                {checkoutBusy === 'lifetime' ? 'REDIRECTING...' : 'GET LIFETIME ACCESS'}
              </button>
            )}
          </div>
        </div>

        {canManageBilling && (
          <div style={{ marginTop: 24, padding: '16px 18px', borderRadius: 8, border: '1px solid var(--cyan)22', background: 'rgba(34,211,238,.08)', fontSize: 11, color: 'var(--t2)', lineHeight: 1.8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <span>Recurring customers should cancel or update billing in the Lemon Squeezy portal before changing paid plans.</span>
            <button
              onClick={openBillingPortal}
              style={{ padding: '10px 12px', borderRadius: 6, border: '1px solid var(--cyan)33', background: 'var(--cyan)12', color: 'var(--cyan)', fontSize: 10, fontFamily: 'JetBrains Mono', fontWeight: 800, cursor: 'pointer', letterSpacing: '.12em' }}
            >
              OPEN BILLING PORTAL
            </button>
          </div>
        )}
      </section>

      <style>{`
        @media (max-width: 980px) {
          .dd-pricing-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
